import { MILESTONE_WEEK_AHEAD_DAYS } from "@athena/common/utils/clientEngagement";
import type { ClientRecord } from "./client";
import { daysSince, fullName, parseCalendarDate, toCalendarDate } from "./client";

/**
 * Birthdays and sober anniversaries by calendar month, so a coordinator can order cards and
 * packages ahead rather than a week at a time.
 *
 * Worked out from the directory's clients rather than asked of the API: the directory already
 * loads every client with both dates, and a month is a filter over them.
 */

/** A birthday or sober anniversary, on the day it falls in the month being viewed. */
export interface Milestone {
    client: ClientRecord;
    /** Local midnight of that day. */
    date: Date;
}

export interface SoberAnniversary extends Milestone {
    /** Which anniversary it is: 1 for one year sober. */
    years: number;
}

/** The query parameter the viewed month is kept in, so going to an alum and back keeps it. */
export const MONTH_PARAM = "month";

export const startOfMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 1);

export const addMonths = (month: Date, months: number): Date =>
    new Date(month.getFullYear(), month.getMonth() + months, 1);

/** `YYYY-MM`, the month as `MONTH_PARAM` holds it. */
export const toMonthParam = (month: Date): string =>
    `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`;

/** The month a `YYYY-MM` names, or undefined for anything else. */
export const parseMonthParam = (value: string | null): Date | undefined => {
    const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(value ?? "");
    return match ? new Date(Number(match[1]), Number(match[2]) - 1, 1) : undefined;
};

/**
 * The day a `YYYY-MM-DD` recurs in `month`, or undefined when it falls in another month. February
 * 29 lands on the 28th in a year without one, rather than rolling over into March.
 */
const recurrenceIn = (value: string, month: Date): Date | undefined => {
    const original = parseCalendarDate(value);
    if (original.getMonth() !== month.getMonth()) return undefined;
    const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    return new Date(month.getFullYear(), month.getMonth(), Math.min(original.getDate(), lastDay));
};

/**
 * Everyone a card may go to. Do not contact is left out: it covers a client who asked not to be
 * contacted and one who passed away, and a card would be the contact they refused or would reach a
 * grieving family. Lost contact stays in — a card in the mail is one more way to reach them.
 */
const reachable = (clients: ClientRecord[]): ClientRecord[] =>
    clients.filter((client) => client.status !== "DO_NOT_CONTACT");

const byDayThenName = (a: Milestone, b: Milestone): number =>
    a.date.getTime() - b.date.getTime() || fullName(a.client).localeCompare(fullName(b.client));

export const birthdaysIn = (clients: ClientRecord[], month: Date): Milestone[] =>
    reachable(clients)
        .flatMap((client) => {
            const date = client.birthday ? recurrenceIn(client.birthday, month) : undefined;
            return date ? [{ client, date }] : [];
        })
        .sort(byDayThenName);

/** The two days a milestone is called out on: the day itself, and a week before it. */
export type MilestoneWhen = "today" | "inAWeek";

/** Whole days from `today` to a milestone: 0 today, 1 tomorrow. */
export const daysUntil = (date: Date, today: Date): number => -daysSince(toCalendarDate(date), today);

/** Which callout a milestone `daysAway` belongs to, if either. */
export const milestoneWhen = (daysAway: number): MilestoneWhen | undefined =>
    daysAway === 0 ? "today" : daysAway === MILESTONE_WEEK_AHEAD_DAYS ? "inAWeek" : undefined;

/**
 * Milestones exactly `days` from today. A week out can fall in next month — and, from late
 * December, next year — so this month and next are both read.
 */
const inDays = <M extends Milestone>(inMonth: (month: Date) => M[], today: Date, days: number): M[] => {
    const month = startOfMonth(today);
    return [
        month,
        addMonths(month, 1),
    ]
        .flatMap(inMonth)
        .filter(({ date }) => daysUntil(date, today) === days);
};

/**
 * The milestones still to come: today's and later. The dashboard is for what to send next, so a
 * birthday already gone this month is only noise.
 */
export const upcoming = <M extends Milestone>(milestones: M[], today: Date): M[] => {
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return milestones.filter(({ date }) => date >= startOfToday);
};

/** Whose birthday it is today, whatever month the page is showing. */
export const birthdaysToday = (clients: ClientRecord[], today: Date): Milestone[] =>
    inDays((month) => birthdaysIn(clients, month), today, 0);

/** Birthdays a week from today. */
export const birthdaysInAWeek = (clients: ClientRecord[], today: Date): Milestone[] =>
    inDays((month) => birthdaysIn(clients, month), today, MILESTONE_WEEK_AHEAD_DAYS);

export const soberAnniversariesIn = (clients: ClientRecord[], month: Date): SoberAnniversary[] =>
    reachable(clients)
        .flatMap((client) => {
            const { soberDate } = client;
            const date = soberDate ? recurrenceIn(soberDate, month) : undefined;
            if (!soberDate || !date) return [];
            const years = month.getFullYear() - parseCalendarDate(soberDate).getFullYear();
            // The month they got sober, or a sober date still ahead, is no anniversary yet.
            return years >= 1 ? [{ client, date, years }] : [];
        })
        .sort(byDayThenName);

/** Whose sober anniversary it is today, whatever month the page is showing. */
export const soberAnniversariesToday = (clients: ClientRecord[], today: Date): SoberAnniversary[] =>
    inDays((month) => soberAnniversariesIn(clients, month), today, 0);

/** Sober anniversaries a week from today. */
export const soberAnniversariesInAWeek = (clients: ClientRecord[], today: Date): SoberAnniversary[] =>
    inDays((month) => soberAnniversariesIn(clients, month), today, MILESTONE_WEEK_AHEAD_DAYS);

const MONTH_FORMAT = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" });
const MONTH_NAME_FORMAT = new Intl.DateTimeFormat("en-US", { month: "long" });
const MILESTONE_DAY_FORMAT = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });

/** "July 2026". */
export const formatMonth = (month: Date): string => MONTH_FORMAT.format(month);
/** "July". */
export const formatMonthName = (month: Date): string => MONTH_NAME_FORMAT.format(month);
/** "Jul 30" — the year is the month's, shown above. */
export const formatMilestoneDay = (date: Date): string => MILESTONE_DAY_FORMAT.format(date);
